import { useQuery } from "react-query";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ButtonWrapper } from "styles/ModalCommonStyled";
import BigButton from "components/atoms/buttons/BigButton";
import {
  CpRequestInfo,
  EuRequestResponse,
  SUBSCRIPTION_TYPE,
  SubscriptionRequestList,
  SubscriptionRequestListResponse,
  SubscriptionRequestTable,
} from "api/interfaces/subscription.interface";
import {
  getSubscriptonRequestList,
  postRequestConfirmKeyType,
} from "api/subscriptionAPI";
import useStatusMutation from "hook/useStatusMutation";
import EmailToRequesterModal from "../../../emailToRequester";
import { useEffect, useMemo, useState } from "react";
import CpRequestHistory from "./CpRequestHistory";
import { initialQueryKeyInfo } from "hook/subscription/useKeyTable";
import SubscriptionOrgLabel from "components/atoms/text/labels/SubscriptionOrgLabel";
import useApiError from "hook/useApiError";
import { useAppSelector } from "redux/hooks";
import { selectSubscriptionStomp } from "redux/stomp/stompSelector";
import { BasePagination } from "api/interfaces/commonInterface.interface";
import Spinner from "components/atoms/loader/Spinner";

type Props = {
  requestInfo: CpRequestInfo;
  onModal: () => void;
  refetch: () => void;
};

export default function CpRequestCheckModal(props: Props): JSX.Element {
  const [queryInfo, setQueryInfo] = useState<BasePagination>({
    ...initialQueryKeyInfo,
    pageLimit: 100,
  });
  const { subscriptionRequestUpdated, subscriptionConfirmUpdated } =
    useAppSelector(selectSubscriptionStomp);
  const { notifySuccess, notifyError, lang } = useApiError();

  const { data, refetch, isLoading, isFetching } = useQuery(
    [
      "getSubscriptonRequestList",
      props.requestInfo.accountId,
      queryInfo,
      subscriptionConfirmUpdated,
    ],

    () =>
      getSubscriptonRequestList({
        accountId: props.requestInfo.accountId,
        payload: queryInfo,
        isConfirm: false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: SubscriptionRequestListResponse) => {
        if (res.page) {
          setQueryInfo((prev) => {
            return {
              ...prev,
              ...res.page,
            };
          });
        }
      },
    }
  );

  useEffect(() => {
    if (subscriptionRequestUpdated.accountId === props.requestInfo.accountId) {
      refetch();
    }
  }, [
    subscriptionRequestUpdated,
    subscriptionConfirmUpdated,
    props.requestInfo.accountId,
  ]);

  const mutateConfirm = useStatusMutation(
    postRequestConfirmKeyType,

    {
      onSuccess: async (res: EuRequestResponse) => {
        notifySuccess(lang.label.subscription.confirm.success);
        props.refetch();

        // request list 재조회 + 결과로 모달 닫기 판단
        const { data: refreshedList } = await refetch();

        const emptyAfterConfirm =
          refreshedList?.error === 0 &&
          Array.isArray(refreshedList?.result) &&
          refreshedList.result.length === 0;

        if (emptyAfterConfirm) {
          props.onModal(); // 닫기
        }
      },
      onError: (_e: any) => {
        notifyError(lang.label.subscription.confirm.fail);
      },
    }
  );

  const onConfirm = (row: SubscriptionRequestTable) => {
    mutateConfirm.mutate({
      accountId: props.requestInfo.accountId,
      type: row.keyType,
      payload: { confirmId: [row.id] },
    });
  };

  const [requestItem, setRequestItem] =
    useState<SubscriptionRequestTable | null>(null);

  const onCloseEmailToRequesterModal = () => {
    setRequestItem(null);
  };

  const onOpenEmailToRequesterModal = (item: SubscriptionRequestTable) => {
    setRequestItem(item);
  };

  const requestList: SubscriptionRequestTable[] = useMemo(() => {
    if (data && data.result && data.error === 0) {
      return data.result.map((res: SubscriptionRequestList) => {
        return {
          ...res,
          qty:
            res.keyType === SUBSCRIPTION_TYPE.STORAGE
              ? res.capacity
              : res.channels,
        };
      });
    }
    return [];
  }, [data]);

  if (requestItem) {
    return (
      <EmailToRequesterModal
        onModal={props.onModal}
        onCloseEmailToRequesterModal={onCloseEmailToRequesterModal}
        requestInfo={props.requestInfo}
        requestItem={requestItem}
      />
    );
  }

  return (
    <BigPopupTemplate title="Request Subscription Key" onModal={props.onModal}>
      <SubscriptionOrgLabel
        accountName={props.requestInfo.accountName}
        accountNumber={props.requestInfo.accountNumber}
        accountLevel={props.requestInfo.accountLevel}
      />

      <CpRequestHistory
        requestList={requestList}
        onOpenEmailToRequesterModal={onOpenEmailToRequesterModal}
        onConfirm={onConfirm}
      />

      {(isLoading || isFetching) && <Spinner />}

      <ButtonWrapper>
        <BigButton
          label={"Cancel"}
          buttonType="cancel"
          onClickBtn={props.onModal}
          disabled={!mutateConfirm.idle}
          data-testid="subscription-modal-cp-request-button-modal"
        />
      </ButtonWrapper>
    </BigPopupTemplate>
  );
}
